/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView, TouchableOpacity,
  Image, Dimensions, Linking, Alert, TextInput, Share,
  ActivityIndicator, Platform, Modal, Animated,
} from 'react-native';
import {
  Ionicons, MaterialCommunityIcons, MaterialIcons,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import MapView, { Marker } from 'react-native-maps';

const { width } = Dimensions.get('window');

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const ANTHROPIC_API_KEY = 'YOUR_ANTHROPIC_API_KEY'; // Use a backend proxy in production

// ─────────────────────────────────────────────────────────────────────────────
// LOGO
// ─────────────────────────────────────────────────────────────────────────────
const JobengLogo = () => (
  <Image source={require('../assets/jobeng_icon.png')} style={{ width: 30, height: 30 }} resizeMode="contain" />
);

// ─────────────────────────────────────────────────────────────────────────────
// UK CITY COORDINATES
// ─────────────────────────────────────────────────────────────────────────────
const UK_CITY_COORDINATES = {
  'London':      { latitude: 51.5074, longitude: -0.1278 },
  'Manchester':  { latitude: 53.4808, longitude: -2.2426 },
  'Birmingham':  { latitude: 52.4862, longitude: -1.8904 },
  'Glasgow':     { latitude: 55.8642, longitude: -4.2518 },
  'Edinburgh':   { latitude: 55.9533, longitude: -3.1883 },
  'Liverpool':   { latitude: 53.4084, longitude: -2.9916 },
  'Leeds':       { latitude: 53.8008, longitude: -1.5491 },
  'Bristol':     { latitude: 51.4545, longitude: -2.5879 },
  'Sheffield':   { latitude: 53.3811, longitude: -1.4701 },
  'Cardiff':     { latitude: 51.4816, longitude: -3.1791 },
  'Belfast':     { latitude: 54.5973, longitude: -5.9301 },
  'Newcastle':   { latitude: 54.9783, longitude: -1.6178 },
  'Nottingham':  { latitude: 52.9548, longitude: -1.1581 },
  'Southampton': { latitude: 50.9097, longitude: -1.4044 },
};

// ─────────────────────────────────────────────────────────────────────────────
// DATA HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const getEventColor = (type) => ({
  'Job Fair': '#19abb5', 'Career Fair': '#0e4e96', 'Networking Event': '#f59e0b',
  'Industry Conference': '#8b5cf6', 'Workshop': '#fdb913', 'Webinar': '#10b981',
  'Company Open Day': '#ef4444', 'Tech Talk': '#3b82f6', 'Hackathon': '#6d28d9',
  'Industry Exhibition': '#06b6d4', 'Apprenticeship Fair': '#db2777',
}[type] || '#0e4e96');

const getEventIcon = (type) => ({
  'Job Fair': 'briefcase-search', 'Career Fair': 'account-tie', 'Networking Event': 'account-group',
  'Industry Conference': 'microphone-variant', 'Workshop': 'hammer-wrench', 'Webinar': 'video',
  'Company Open Day': 'office-building', 'Tech Talk': 'presentation', 'Hackathon': 'code-braces',
  'Industry Exhibition': 'view-gallery', 'Apprenticeship Fair': 'school',
}[type] || 'calendar-month');

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const formatShortDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

// ─────────────────────────────────────────────────────────────────────────────
// FETCH EVENT DETAILS
// ─────────────────────────────────────────────────────────────────────────────
const fetchEventDetails = async (eventId, source) => {
  try {
    return {
      id: eventId,
      title: 'UK Engineering Career Conference 2025',
      type: 'Industry Conference',
      date: '2025-06-15',
      time: '09:00 - 17:00',
      location: 'ExCeL London, Royal Victoria Dock, London',
      organizer: 'UK Engineering Council',
      description: 'The premier engineering career event in the UK, bringing together leading engineering companies, recruiters, and professionals.',
      longDescription: `Join us for the UK's largest engineering career event at ExCeL London. This conference is designed for engineering professionals at all career stages — from graduates to senior executives.\n\nKey Features:\n• 100+ exhibiting companies including Rolls-Royce, BAE Systems, Siemens, Arup, and Atkins\n• 50+ technical sessions and workshops\n• Networking with 5,000+ engineering professionals\n• CV clinics and interview practice sessions\n• CPD accredited: 8 hours of Continuing Professional Development`,
      organizerContact: 'events@engineeringcouncil.org.uk',
      organizerPhone: '+44 20 7123 4567',
      organizerWebsite: 'https://www.engineeringcouncil.org.uk',
      organizerLinkedIn: 'https://www.linkedin.com/company/engineering-council',
      organizerTwitter: 'https://twitter.com/EngCouncilUK',
      website: 'https://www.engineeringcouncil.org.uk/events',
      coordinates: { latitude: 51.5079, longitude: 0.0281 },
      address: 'ExCeL London, Royal Victoria Dock, London E16 1XL',
      isOnline: false,
      onlineLink: null,
      speakers: [
        { id: 's1', name: 'Dr. Sarah Chen', title: 'Chief Technology Officer, Rolls-Royce', topic: 'Sustainable Aviation and Future Engineering', time: '10:00 - 10:45', bio: 'Leading Rolls-Royce\'s technology strategy with 20+ years in aerospace engineering.', image: null, linkedin: '#' },
        { id: 's2', name: 'James Wilson', title: 'Director of Engineering, Arup', topic: 'Digital Transformation in Civil Engineering', time: '11:00 - 11:45', bio: 'Over 25 years experience in major infrastructure projects worldwide.', image: null, linkedin: '#' },
        { id: 's3', name: 'Priya Patel', title: 'Head of AI Research, DeepMind', topic: 'AI in Engineering Design', time: '13:30 - 14:15', bio: 'Leading AI research with applications in engineering design optimisation.', image: null, linkedin: '#' },
        { id: 's4', name: 'Tom Bradley', title: 'Engineering Director, National Grid', topic: 'Net Zero: Engineering the Energy Transition', time: '14:30 - 15:15', bio: 'Leading National Grid\'s engineering teams in the UK energy transition.', image: null, linkedin: '#' },
      ],
      agenda: [
        { time: '09:00 - 09:30', activity: 'Registration & Welcome Coffee', type: 'break' },
        { time: '09:30 - 10:00', activity: 'Opening Keynote: Future of UK Engineering', type: 'keynote' },
        { time: '10:00 - 10:45', activity: 'Sustainable Aviation – Dr. Sarah Chen', type: 'talk' },
        { time: '11:00 - 11:45', activity: 'Digital Transformation in Civil Engineering – James Wilson', type: 'talk' },
        { time: '12:00 - 13:00', activity: 'Networking Lunch', type: 'break' },
        { time: '13:30 - 14:15', activity: 'AI in Engineering Design – Priya Patel', type: 'talk' },
        { time: '14:30 - 15:15', activity: 'Net Zero Engineering – Tom Bradley', type: 'talk' },
        { time: '15:15 - 16:00', activity: 'Company Exhibitions & Recruitment', type: 'networking' },
        { time: '16:00 - 17:00', activity: 'Closing Panel & Networking Reception', type: 'networking' },
      ],
      exhibitors: [
        'Rolls-Royce plc', 'BAE Systems', 'Siemens UK', 'Arup', 'AtkinsRéalis',
        'Jacobs', 'National Grid', 'Network Rail', 'Jaguar Land Rover', 'Airbus UK',
        'BP', 'Shell', 'Google UK', 'Microsoft UK', 'Amazon Web Services',
        'IBM UK', 'ARM Holdings', 'Renishaw', 'Mott MacDonald', 'WSP',
      ],
      documents: [
        { id: 'd1', name: 'Conference Programme.pdf', size: '2.4 MB', url: '#', icon: 'file-pdf-box' },
        { id: 'd2', name: 'Speaker Bios.pdf', size: '1.8 MB', url: '#', icon: 'file-account' },
        { id: 'd3', name: 'Venue Map.pdf', size: '3.2 MB', url: '#', icon: 'map' },
        { id: 'd4', name: 'Exhibitor Guide.pdf', size: '4.1 MB', url: '#', icon: 'office-building' },
      ],
      photos: [
        { id: 'p1', url: null, caption: '2024 Conference Opening', thumbnail: null },
        { id: 'p2', url: null, caption: 'Networking Session', thumbnail: null },
        { id: 'p3', url: null, caption: 'Exhibition Hall', thumbnail: null },
        { id: 'p4', url: null, caption: 'Workshop Session', thumbnail: null },
      ],
      faqs: [
        { question: 'Is this event free to attend?', answer: 'Registration is free for engineering professionals. Some premium workshops may have additional fees.' },
        { question: 'Is there parking available?', answer: 'Yes, ExCeL London has extensive parking facilities. Pre-booking is recommended.' },
        { question: 'Will there be graduate recruitment opportunities?', answer: 'Yes, many exhibiting companies are actively recruiting graduates with dedicated graduate program representatives.' },
        { question: 'Is the venue accessible?', answer: 'ExCeL London is fully accessible with step-free access, accessible toilets, and hearing loop systems.' },
        { question: 'Can I get CPD points for attending?', answer: 'Yes, this event qualifies for 8 hours of CPD. Certificates will be provided upon request.' },
        { question: 'What should I bring?', answer: 'Bring printed CVs, business cards or a LinkedIn QR code, and any relevant portfolio materials. Smart-casual dress is recommended.' },
      ],
      relatedEvents: [
        { id: 're1', title: 'Women in Engineering Summit', type: 'Conference', date: '2025-07-10', location: 'Manchester Central' },
        { id: 're2', title: 'Civil Engineering Career Fair', type: 'Job Fair', date: '2025-08-22', location: 'Birmingham NEC' },
        { id: 're3', title: 'Tech Industry Networking', type: 'Networking Event', date: '2025-06-05', location: 'London Tech Hub' },
      ],
      reviews: [
        { id: 'r1', author: 'Alex M.', rating: 5, comment: 'Exceptional event — connected with 3 potential employers and landed an interview the next week.', date: '2024-06-20', verified: true },
        { id: 'r2', author: 'Sarah K.', rating: 4, comment: 'Really well organised. The workshop sessions were the highlight. Would have liked more civil engineering companies.', date: '2024-06-21', verified: true },
        { id: 'r3', author: 'James T.', rating: 5, comment: 'Best engineering networking event in the UK. The speaker lineup was outstanding.', date: '2024-06-22', verified: false },
      ],
      attendees: 1250,
      maxAttendees: 2000,
      isRegistered: false,
      price: 'Free',
      source: source || 'professional',
      isRealEvent: true,
      cpdHours: 8,
      ukQualifications: ['CEng', 'IEng', 'EngTech'],
      professionalBodies: ['IET', 'ICE', 'IMechE', 'BCS'],
      sectors: ['Aerospace', 'Civil', 'Mechanical', 'Electrical', 'Software', 'Renewable Energy'],
      transportLinks: {
        tube: ['DLR: Custom House (5 min walk)', 'Elizabeth Line: Custom House'],
        rail: 'London City Airport (10 min taxi)',
        bus: 'Routes 473, 300, 325',
      },
      accommodation: [
        { name: 'Aloft ExCeL London', note: 'On-site', url: '#' },
        { name: 'Holiday Inn Express Royal Docks', note: '5 min walk', url: '#' },
        { name: 'Novotel London ExCeL', note: 'Adjacent', url: '#' },
      ],
    };
  } catch (err) {
    console.error('fetchEventDetails error:', err);
    return null;
  }
};

const enhanceEventData = (event) => {
  const city = event.location?.split(',').pop().trim();
  const coordinates = UK_CITY_COORDINATES[city] || { latitude: 51.5074, longitude: -0.1278 };
  return {
    ...event,
    longDescription:    event.longDescription || event.description,
    organizerContact:   event.organizerContact || 'info@organizer.co.uk',
    organizerPhone:     event.organizerPhone || '+44 20 7123 4567',
    website:            event.website || 'https://example.com',
    coordinates:        event.coordinates || coordinates,
    address:            event.address || event.location,
    speakers:           event.speakers || [],
    agenda:             event.agenda || [],
    exhibitors:         event.exhibitors || [],
    documents:          event.documents || [],
    photos:             event.photos || [],
    faqs:               event.faqs || [],
    relatedEvents:      event.relatedEvents || [],
    reviews:            event.reviews || [],
    attendees:          event.attendees || 100,
    maxAttendees:       event.maxAttendees || 200,
    isRegistered:       event.isRegistered || false,
    cpdHours:           event.cpdHours || 0,
    ukQualifications:   event.ukQualifications || [],
    professionalBodies: event.professionalBodies || [],
    transportLinks:     event.transportLinks || {},
    accommodation:      event.accommodation || [],
    sectors:            event.sectors || [],
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// CLAUDE AI ADVICE
// ─────────────────────────────────────────────────────────────────────────────
const getClaudeEventAdvice = async (eventDetails, userProfile = {}) => {
  const { experienceLevel = 'mid' } = userProfile;
  if (ANTHROPIC_API_KEY === 'YOUR_ANTHROPIC_API_KEY') {
    const templates = {
      student:  `As a student attending ${eventDetails.title}:\n\n• Research companies and prepare specific questions\n• Bring multiple copies of your CV and a notebook\n• Attend all graduate scheme information sessions\n• Connect with recent graduates at target companies\n• Follow up with every contact within 48 hours via LinkedIn`,
      graduate: `Graduate at ${eventDetails.title}:\n\n• Focus on companies with structured graduate programmes\n• Prepare your "engineering journey" story (2 minutes)\n• Ask about professional development support and chartership\n• Network with mid-level engineers for mentorship\n• Log your attendance for CPD — this event counts for ${eventDetails.cpdHours || 8} hours`,
      mid:      `Mid-career engineer at ${eventDetails.title}:\n\n• Target senior engineer and manager-level opportunities\n• Discuss specific projects and technical achievements\n• Explore companies investing in your speciality\n• Connect with hiring managers and team leaders directly\n• Consider proposing a speaking topic for next year`,
      senior:   `Senior engineer/manager at ${eventDetails.title}:\n\n• Focus on strategic networking with directors and VPs\n• Discuss leadership opportunities and team building\n• Explore companies undergoing digital transformation\n• Consider consulting, advisory, or NED opportunities\n• Look for potential collaboration or business development prospects`,
      executive:`C-suite/executive at ${eventDetails.title}:\n\n• Connect with other C-suite attendees and board-level contacts\n• Explore M&A, partnership, and strategic alliance opportunities\n• Represent your organisation as a thought leader\n• Identify future talent pipelines early\n• Assess the competitive landscape across exhibiting companies`,
    };
    return templates[experienceLevel] || templates.mid;
  }
  try {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': ANTHROPIC_API_KEY,
        'anthropic-version': '2023-06-01',
      },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 500,
        messages: [{
          role: 'user',
          content: `Provide concise, actionable career advice (5-7 bullet points, max 250 words) for a ${experienceLevel}-level engineer attending "${eventDetails.title}" in the UK. The event features ${eventDetails.speakers?.length || 0} speakers, ${eventDetails.exhibitors?.length || 0} exhibiting companies, and offers ${eventDetails.cpdHours || 0} CPD hours. Sectors: ${eventDetails.sectors?.join(', ')}. Focus on maximising networking ROI, key sessions to attend, and follow-up strategy.`,
        }],
      }),
    });
    const data = await response.json();
    return data.content?.[0]?.text || 'Unable to load AI advice at this time.';
  } catch (err) {
    return `AI advice unavailable. Focus on networking and preparing your elevator pitch for ${eventDetails.title}.`;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// COUNTDOWN HOOK
// ─────────────────────────────────────────────────────────────────────────────
const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, isPast: false });
  useEffect(() => {
    const tick = () => {
      const now  = new Date();
      const end  = new Date(targetDate);
      const diff = end - now;
      if (diff <= 0) { setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true }); return; }
      setTimeLeft({
        days:    Math.floor(diff / 86400000),
        hours:   Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        isPast:  false,
      });
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);
  return timeLeft;
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────────────────────
export default function EventDetailScreen() {
  const navigation = useNavigation();
  const route      = useRoute();
  const { event, eventData, eventId, source, userProfile } = route.params || {};

  // Core
  const [eventDetails, setEventDetails] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [activeTab,    setActiveTab]    = useState('About');

  // Feature states
  const [isSaved,          setIsSaved]          = useState(false);
  const [notifyEnabled,    setNotifyEnabled]    = useState(false);
  const [aiAdvice,         setAiAdvice]         = useState('');
  const [isAiLoading,      setIsAiLoading]      = useState(false);
  const [showAllExhibitors,setShowAllExhibitors]= useState(false);
  const [showAllAgenda,    setShowAllAgenda]    = useState(false);
  const [userNotes,        setUserNotes]        = useState('');
  const [editingNotes,     setEditingNotes]     = useState(false);
  const [userRating,       setUserRating]       = useState(0);
  const [reviewText,       setReviewText]       = useState('');
  const [reviews,          setReviews]          = useState([]);
  const [expandedFaq,      setExpandedFaq]      = useState(null);
  const [newQuestion,      setNewQuestion]      = useState('');
  const [showShareModal,   setShowShareModal]   = useState(false);
  const [showRegModal,     setShowRegModal]     = useState(false);
  const [confirmationNo,   setConfirmationNo]   = useState('');
  const [selectedPhoto,    setSelectedPhoto]    = useState(null);
  const [photoModal,       setPhotoModal]       = useState(false);
  const [experienceLevel,  setExperienceLevel]  = useState('mid');

  const countdown = useCountdown(eventDetails?.date);
  const TABS = ['About', 'Agenda', 'Speakers', 'Exhibitors', 'Photos', 'Travel', 'Reviews', 'Notes', 'Q&A'];

  // ── Load ───────────────────────────────────────────────────────────────────
  useEffect(() => { loadEventDetails(); }, []);
  useEffect(() => { if (eventDetails) { loadAiAdvice(); setReviews(eventDetails.reviews || []); } }, [eventDetails?.id]);

  const loadEventDetails = async () => {
    setLoading(true);
    try {
      let loaded = null;
      if (event || eventData) {
        loaded = enhanceEventData(event || eventData);
      } else if (eventId) {
        loaded = await fetchEventDetails(eventId, source);
      }
      if (loaded) setEventDetails(loaded);
      else { Alert.alert('Error', 'Could not load event details'); navigation.goBack(); }
    } catch (err) {
      console.error(err);
      Alert.alert('Error', 'Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const loadAiAdvice = async () => {
    if (!eventDetails) return;
    setIsAiLoading(true);
    try {
      const advice = await getClaudeEventAdvice(eventDetails, { ...userProfile, experienceLevel });
      setAiAdvice(advice);
    } catch { setAiAdvice('AI advice unavailable. Focus on networking and preparing your elevator pitch.'); }
    finally { setIsAiLoading(false); }
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleRegister = () => {
    if (eventDetails.isRegistered) {
      Alert.alert('Cancel Registration', `Cancel your registration for "${eventDetails.title}"?`, [
        { text: 'Keep Registration', style: 'cancel' },
        { text: 'Cancel', style: 'destructive', onPress: () => {
          setEventDetails(prev => ({ ...prev, isRegistered: false }));
          Alert.alert('Cancelled', 'Your registration has been cancelled.');
        }},
      ]);
    } else {
      const ref = 'JOBENG-' + Math.random().toString(36).slice(2,9).toUpperCase();
      setConfirmationNo(ref);
      setShowRegModal(true);
    }
  };

  const confirmRegister = () => {
    setEventDetails(prev => ({ ...prev, isRegistered: true }));
    setShowRegModal(false);
    Alert.alert('Registered! 🎉', `Confirmation: ${confirmationNo}\n\nA confirmation has been sent to your email.`);
  };

  const handleAddToCalendar = () => {
    if (!eventDetails) return;
    const title    = encodeURIComponent(eventDetails.title);
    const location = encodeURIComponent(eventDetails.location);
    const notes    = encodeURIComponent(eventDetails.description);
    const [startT, endT] = (eventDetails.time || '09:00 - 17:00').split(' - ');
    const start = new Date(`${eventDetails.date}T${startT}:00`).toISOString().replace(/[-:.]|(\d{3}Z)/g, (m,p1) => p1 ? 'Z' : '');
    const end   = new Date(`${eventDetails.date}T${endT}:00`).toISOString().replace(/[-:.]|(\d{3}Z)/g, (m,p1) => p1 ? 'Z' : '');
    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${notes}&location=${location}&sf=true&output=xml`;
    Linking.openURL(url).catch(() => Alert.alert('Error', 'Could not open calendar app.'));
  };

  const handleShare = async () => { setShowShareModal(true); };

  const shareVia = async (method) => {
    if (!eventDetails) return;
    const msg = `${eventDetails.title} — ${formatShortDate(eventDetails.date)} at ${eventDetails.location}\n\n${eventDetails.website}`;
    setShowShareModal(false);
    switch (method) {
      case 'native':   Share.share({ message: msg, title: eventDetails.title }); break;
      case 'whatsapp': Linking.openURL(`whatsapp://send?text=${encodeURIComponent(msg)}`); break;
      case 'linkedin': Linking.openURL(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(eventDetails.website)}`); break;
      case 'email':    Linking.openURL(`mailto:?subject=${encodeURIComponent(eventDetails.title)}&body=${encodeURIComponent(msg)}`); break;
      case 'copy':     Alert.alert('Copied!', 'Event link copied to clipboard'); break;
    }
  };

  const handleOpenMap = () => {
    if (!eventDetails?.coordinates) return;
    const { latitude, longitude } = eventDetails.coordinates;
    const label = encodeURIComponent(eventDetails.location);
    const url = Platform.select({
      ios:     `maps:0,0?q=${label}@${latitude},${longitude}`,
      android: `geo:0,0?q=${latitude},${longitude}(${label})`,
    });
    Linking.openURL(url);
  };

  const submitReview = () => {
    if (!userRating) { Alert.alert('Rating required', 'Please select a star rating.'); return; }
    const newReview = {
      id: `user_${Date.now()}`, author: 'You', rating: userRating,
      comment: reviewText, date: new Date().toISOString().split('T')[0], verified: false,
    };
    setReviews(prev => [newReview, ...prev]);
    setUserRating(0); setReviewText('');
    Alert.alert('Review submitted!', 'Thank you for your feedback.');
  };

  const submitQuestion = () => {
    if (!newQuestion.trim()) return;
    Alert.alert('Question Submitted', 'Your question has been sent to the organiser.');
    setNewQuestion('');
  };

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : '—';

  // ── TAB RENDERERS ──────────────────────────────────────────────────────────
  const renderAboutTab = () => (
    <View style={styles.tabContent}>
      {/* Description */}
      <Text style={styles.sectionTitle}>About This Event</Text>
      <Text style={styles.description}>{eventDetails.longDescription}</Text>

      {/* Key Details */}
      <Text style={styles.sectionTitle}>Event Details</Text>
      <View style={styles.detailsCard}>
        {[
          { icon: 'calendar',       text: formatDate(eventDetails.date) },
          { icon: 'clock-outline',  text: eventDetails.time },
          { icon: 'map-marker',     text: eventDetails.location },
          { icon: 'currency-gbp',   text: eventDetails.price || 'Free' },
          { icon: 'account-group',  text: `${eventDetails.attendees} / ${eventDetails.maxAttendees} registered` },
          ...(eventDetails.cpdHours ? [{ icon: 'certificate', text: `${eventDetails.cpdHours} CPD hours accredited` }] : []),
        ].map((d, i) => (
          <View key={i} style={styles.detailItem}>
            <View style={styles.detailIconWrap}><MaterialCommunityIcons name={d.icon} size={20} color="#0e4e96" /></View>
            <Text style={styles.detailText}>{d.text}</Text>
          </View>
        ))}
      </View>

      {/* Attendee capacity bar */}
      {eventDetails.maxAttendees > 0 && (
        <View style={styles.capacityContainer}>
          <View style={styles.capacityRow}>
            <Text style={styles.capacityLabel}>Capacity</Text>
            <Text style={styles.capacityPct}>{Math.round((eventDetails.attendees / eventDetails.maxAttendees) * 100)}% full</Text>
          </View>
          <View style={styles.capacityBar}>
            <View style={[styles.capacityFill, {
              width: `${Math.min(100, (eventDetails.attendees / eventDetails.maxAttendees) * 100)}%`,
              backgroundColor: (eventDetails.attendees / eventDetails.maxAttendees) > 0.85 ? '#ef4444' : '#0e4e96',
            }]} />
          </View>
          <Text style={styles.capacityNote}>{eventDetails.maxAttendees - eventDetails.attendees} spots remaining</Text>
        </View>
      )}

      {/* Organiser */}
      <Text style={styles.sectionTitle}>Organiser</Text>
      <View style={styles.organizerCard}>
        <View style={styles.organizerIcon}><MaterialCommunityIcons name="office-building" size={28} color="#0e4e96" /></View>
        <View style={styles.organizerInfo}>
          <Text style={styles.organizerName}>{eventDetails.organizer}</Text>
          <View style={styles.contactRow}>
            <TouchableOpacity style={styles.contactBtn} onPress={() => Linking.openURL(`mailto:${eventDetails.organizerContact}`)}>
              <MaterialCommunityIcons name="email" size={14} color="#fff" />
              <Text style={styles.contactBtnText}>Email</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.contactBtn} onPress={() => Linking.openURL(`tel:${eventDetails.organizerPhone}`)}>
              <MaterialCommunityIcons name="phone" size={14} color="#fff" />
              <Text style={styles.contactBtnText}>Call</Text>
            </TouchableOpacity>
            {eventDetails.organizerLinkedIn && (
              <TouchableOpacity style={[styles.contactBtn, { backgroundColor: '#0A66C2' }]} onPress={() => Linking.openURL(eventDetails.organizerLinkedIn)}>
                <MaterialCommunityIcons name="linkedin" size={14} color="#fff" />
                <Text style={styles.contactBtnText}>LinkedIn</Text>
              </TouchableOpacity>
            )}
            {eventDetails.organizerTwitter && (
              <TouchableOpacity style={[styles.contactBtn, { backgroundColor: '#1DA1F2' }]} onPress={() => Linking.openURL(eventDetails.organizerTwitter)}>
                <MaterialCommunityIcons name="twitter" size={14} color="#fff" />
                <Text style={styles.contactBtnText}>Twitter</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>

      {/* Sectors */}
      {eventDetails.sectors?.length > 0 && (
        <>
          <Text style={styles.sectionTitle}>Industry Sectors</Text>
          <View style={styles.badgeWrap}>
            {eventDetails.sectors.map((s, i) => (
              <View key={i} style={styles.sectorBadge}><Text style={styles.sectorBadgeText}>{s}</Text></View>
            ))}
          </View>
        </>
      )}

      {/* UK Qualifications */}
      {eventDetails.ukQualifications?.length > 0 && (
        <>
          <Text style={styles.sectionTitle}>Relevant UK Qualifications</Text>
          <View style={styles.badgeWrap}>
            {eventDetails.ukQualifications.map((q, i) => (
              <View key={i} style={styles.qualBadge}><Text style={styles.qualBadgeText}>{q}</Text></View>
            ))}
          </View>
        </>
      )}

      {/* Professional Bodies */}
      {eventDetails.professionalBodies?.length > 0 && (
        <>
          <Text style={styles.sectionTitle}>Professional Bodies</Text>
          <View style={styles.badgeWrap}>
            {eventDetails.professionalBodies.map((b, i) => (
              <View key={i} style={styles.bodyBadge}><Text style={styles.bodyBadgeText}>{b}</Text></View>
            ))}
          </View>
        </>
      )}

      {/* Documents */}
      {eventDetails.documents?.length > 0 && (
        <>
          <Text style={styles.sectionTitle}>Downloads</Text>
          {eventDetails.documents.map(doc => (
            <TouchableOpacity key={doc.id} style={styles.documentRow} onPress={() => Linking.openURL(doc.url)}>
              <MaterialCommunityIcons name={doc.icon || 'file-pdf-box'} size={28} color="#ef4444" />
              <View style={styles.documentInfo}>
                <Text style={styles.documentName}>{doc.name}</Text>
                <Text style={styles.documentSize}>{doc.size}</Text>
              </View>
              <MaterialCommunityIcons name="download" size={22} color="#0e4e96" />
            </TouchableOpacity>
          ))}
        </>
      )}
    </View>
  );

  const renderAgendaTab = () => {
    const agendaColors = { keynote: '#8b5cf6', talk: '#0e4e96', break: '#f59e0b', networking: '#19abb5' };
    return (
      <View style={styles.tabContent}>
        <Text style={styles.sectionTitle}>Full Schedule</Text>
        {eventDetails.agenda.slice(0, showAllAgenda ? eventDetails.agenda.length : 5).map((item, i) => (
          <View key={i} style={styles.agendaRow}>
            <View style={[styles.agendaDot, { backgroundColor: agendaColors[item.type] || '#0e4e96' }]} />
            <View style={styles.agendaTimeCol}>
              <Text style={styles.agendaTime}>{item.time}</Text>
            </View>
            <View style={styles.agendaActivityCol}>
              <Text style={styles.agendaActivity}>{item.activity}</Text>
              {item.type && <View style={[styles.agendaTypeBadge, { backgroundColor: (agendaColors[item.type] || '#0e4e96') + '22' }]}>
                <Text style={[styles.agendaTypeText, { color: agendaColors[item.type] || '#0e4e96' }]}>{item.type}</Text>
              </View>}
            </View>
          </View>
        ))}
        {eventDetails.agenda.length > 5 && (
          <TouchableOpacity style={styles.showMoreBtn} onPress={() => setShowAllAgenda(!showAllAgenda)}>
            <Text style={styles.showMoreText}>{showAllAgenda ? 'Show Less' : `Show Full Agenda (${eventDetails.agenda.length} items)`}</Text>
            <MaterialIcons name={showAllAgenda ? 'expand-less' : 'expand-more'} size={20} color="#0e4e96" />
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderSpeakersTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>{eventDetails.speakers.length} Featured Speakers</Text>
      {eventDetails.speakers.map((speaker, i) => (
        <View key={i} style={styles.speakerCard}>
          <View style={styles.speakerAvatarWrap}>
            {speaker.image
              ? <Image source={{ uri: speaker.image }} style={styles.speakerAvatar} />
              : <View style={styles.speakerAvatarPlaceholder}><Text style={styles.speakerInitial}>{speaker.name.charAt(0)}</Text></View>
            }
          </View>
          <View style={styles.speakerInfo}>
            <View style={styles.speakerNameRow}>
              <Text style={styles.speakerName}>{speaker.name}</Text>
              {speaker.linkedin && (
                <TouchableOpacity onPress={() => Linking.openURL(speaker.linkedin)}>
                  <MaterialCommunityIcons name="linkedin" size={18} color="#0A66C2" />
                </TouchableOpacity>
              )}
            </View>
            <Text style={styles.speakerTitle}>{speaker.title}</Text>
            <Text style={styles.speakerTopic}>"{speaker.topic}"</Text>
            <View style={styles.speakerTimeRow}>
              <MaterialCommunityIcons name="clock-outline" size={13} color="#666" />
              <Text style={styles.speakerTimeText}>{speaker.time}</Text>
            </View>
            {speaker.bio && <Text style={styles.speakerBio}>{speaker.bio}</Text>}
          </View>
        </View>
      ))}
    </View>
  );

  const renderExhibitorsTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Exhibiting Companies</Text>
      <Text style={styles.exhibitorCount}>{eventDetails.exhibitors.length} companies attending</Text>
      <View style={styles.exhibitorsGrid}>
        {eventDetails.exhibitors.slice(0, showAllExhibitors ? eventDetails.exhibitors.length : 12).map((e, i) => (
          <View key={i} style={styles.exhibitorChip}>
            <MaterialCommunityIcons name="office-building" size={16} color="#0e4e96" />
            <Text style={styles.exhibitorChipText} numberOfLines={2}>{e}</Text>
          </View>
        ))}
      </View>
      {eventDetails.exhibitors.length > 12 && (
        <TouchableOpacity style={styles.showMoreBtn} onPress={() => setShowAllExhibitors(!showAllExhibitors)}>
          <Text style={styles.showMoreText}>{showAllExhibitors ? 'Show Less' : `Show All (${eventDetails.exhibitors.length})`}</Text>
          <MaterialIcons name={showAllExhibitors ? 'expand-less' : 'expand-more'} size={20} color="#0e4e96" />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderPhotosTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Event Gallery</Text>
      <Text style={styles.gallerySubtitle}>Photos from previous events</Text>
      <View style={styles.photoGrid}>
        {eventDetails.photos.map((photo, i) => (
          <TouchableOpacity key={i} style={styles.photoThumb} onPress={() => { setSelectedPhoto(photo); setPhotoModal(true); }}>
            {photo.url
              ? <Image source={{ uri: photo.url }} style={styles.photoThumbImg} />
              : <View style={[styles.photoThumbPlaceholder, { backgroundColor: '#' + ((i * 31 + 70) * 1234).toString(16).slice(-6) }]}>
                  <MaterialCommunityIcons name="image-outline" size={32} color="rgba(255,255,255,0.6)" />
                </View>
            }
            <Text style={styles.photoCaption} numberOfLines={1}>{photo.caption}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderTravelTab = () => {
    const isOnline = eventDetails.isOnline || eventDetails.location?.toLowerCase().includes('online');
    return (
      <View style={styles.tabContent}>
        <Text style={styles.sectionTitle}>Location & Travel</Text>
        {isOnline ? (
          <View style={styles.onlineCard}>
            <MaterialCommunityIcons name="video-outline" size={40} color="#10b981" />
            <Text style={styles.onlineTitle}>This is an Online Event</Text>
            {eventDetails.onlineLink && (
              <TouchableOpacity style={styles.onlineLinkBtn} onPress={() => Linking.openURL(eventDetails.onlineLink)}>
                <MaterialCommunityIcons name="link" size={16} color="#fff" />
                <Text style={styles.onlineLinkText}>Join Event</Text>
              </TouchableOpacity>
            )}
          </View>
        ) : (
          <>
            <View style={styles.mapContainer}>
              <MapView style={styles.map} initialRegion={{ ...eventDetails.coordinates, latitudeDelta: 0.05, longitudeDelta: 0.05 }}>
                <Marker coordinate={eventDetails.coordinates} title={eventDetails.location} />
              </MapView>
            </View>
            <Text style={styles.addressLabel}>Address</Text>
            <Text style={styles.addressText}>{eventDetails.address}</Text>
            <TouchableOpacity style={styles.directionsBtn} onPress={handleOpenMap}>
              <MaterialCommunityIcons name="directions" size={20} color="#fff" />
              <Text style={styles.directionsBtnText}>Get Directions</Text>
            </TouchableOpacity>
          </>
        )}

        {eventDetails.transportLinks && Object.keys(eventDetails.transportLinks).length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Public Transport</Text>
            <View style={styles.transportCard}>
              {eventDetails.transportLinks.tube && (
                <View style={styles.transportRow}>
                  <View style={[styles.transportIcon, { backgroundColor: '#003399' }]}>
                    <MaterialCommunityIcons name="train" size={14} color="#fff" />
                  </View>
                  <Text style={styles.transportText}>{Array.isArray(eventDetails.transportLinks.tube) ? eventDetails.transportLinks.tube.join(' • ') : eventDetails.transportLinks.tube}</Text>
                </View>
              )}
              {eventDetails.transportLinks.rail && (
                <View style={styles.transportRow}>
                  <View style={[styles.transportIcon, { backgroundColor: '#cc0000' }]}>
                    <MaterialCommunityIcons name="train-variant" size={14} color="#fff" />
                  </View>
                  <Text style={styles.transportText}>{eventDetails.transportLinks.rail}</Text>
                </View>
              )}
              {eventDetails.transportLinks.bus && (
                <View style={styles.transportRow}>
                  <View style={[styles.transportIcon, { backgroundColor: '#cc0000' }]}>
                    <MaterialCommunityIcons name="bus" size={14} color="#fff" />
                  </View>
                  <Text style={styles.transportText}>{eventDetails.transportLinks.bus}</Text>
                </View>
              )}
            </View>
          </>
        )}

        {eventDetails.accommodation?.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Nearby Accommodation</Text>
            {eventDetails.accommodation.map((h, i) => (
              <TouchableOpacity key={i} style={styles.hotelRow} onPress={() => h.url && h.url !== '#' && Linking.openURL(h.url)}>
                <MaterialCommunityIcons name="bed" size={20} color="#0e4e96" />
                <View style={styles.hotelInfo}>
                  <Text style={styles.hotelName}>{typeof h === 'string' ? h : h.name}</Text>
                  {h.note && <Text style={styles.hotelNote}>{h.note}</Text>}
                </View>
                {h.url && h.url !== '#' && <MaterialCommunityIcons name="chevron-right" size={18} color="#999" />}
              </TouchableOpacity>
            ))}
          </>
        )}
      </View>
    );
  };

  const renderReviewsTab = () => (
    <View style={styles.tabContent}>
      {/* Rating Summary */}
      <View style={styles.ratingsSummary}>
        <View style={styles.avgRatingBox}>
          <Text style={styles.avgRatingNumber}>{avgRating}</Text>
          <View style={styles.starsRow}>
            {[1,2,3,4,5].map(s => (
              <MaterialCommunityIcons key={s} name={s <= Math.round(Number(avgRating)) ? 'star' : 'star-outline'} size={18} color="#fbbf24" />
            ))}
          </View>
          <Text style={styles.avgRatingCount}>{reviews.length} reviews</Text>
        </View>
      </View>

      {/* Write Review */}
      <Text style={styles.sectionTitle}>Write a Review</Text>
      <View style={styles.reviewForm}>
        <Text style={styles.reviewFormLabel}>Your Rating</Text>
        <View style={styles.starsRow}>
          {[1,2,3,4,5].map(s => (
            <TouchableOpacity key={s} onPress={() => setUserRating(s)}>
              <MaterialCommunityIcons name={s <= userRating ? 'star' : 'star-outline'} size={32} color="#fbbf24" />
            </TouchableOpacity>
          ))}
        </View>
        <TextInput
          style={styles.reviewInput}
          placeholder="Share your experience..."
          value={reviewText}
          onChangeText={setReviewText}
          multiline
          numberOfLines={4}
        />
        <TouchableOpacity style={styles.submitReviewBtn} onPress={submitReview}>
          <Text style={styles.submitReviewText}>Submit Review</Text>
        </TouchableOpacity>
      </View>

      {/* Reviews List */}
      <Text style={styles.sectionTitle}>All Reviews</Text>
      {reviews.map(r => (
        <View key={r.id} style={styles.reviewCard}>
          <View style={styles.reviewHeader}>
            <View style={styles.reviewAuthorInfo}>
              <View style={styles.reviewAvatar}><Text style={styles.reviewAvatarText}>{r.author.charAt(0)}</Text></View>
              <View>
                <View style={styles.reviewNameRow}>
                  <Text style={styles.reviewAuthor}>{r.author}</Text>
                  {r.verified && <View style={styles.verifiedBadge}><MaterialCommunityIcons name="check-circle" size={12} color="#10b981" /><Text style={styles.verifiedText}>Verified</Text></View>}
                </View>
                <View style={styles.starsRow}>
                  {[1,2,3,4,5].map(s => (
                    <MaterialCommunityIcons key={s} name={s <= r.rating ? 'star' : 'star-outline'} size={14} color="#fbbf24" />
                  ))}
                </View>
              </View>
            </View>
            <Text style={styles.reviewDate}>{r.date}</Text>
          </View>
          <Text style={styles.reviewComment}>{r.comment}</Text>
        </View>
      ))}
    </View>
  );

  const renderNotesTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>My Notes</Text>
      <Text style={styles.notesSubtitle}>Private notes for this event — visible only to you.</Text>
      {editingNotes ? (
        <View>
          <TextInput
            style={styles.notesInput}
            placeholder="Write your notes, prep questions, contacts to meet..."
            value={userNotes}
            onChangeText={setUserNotes}
            multiline
            autoFocus
          />
          <TouchableOpacity style={styles.saveNotesBtn} onPress={() => setEditingNotes(false)}>
            <MaterialCommunityIcons name="content-save" size={16} color="#fff" />
            <Text style={styles.saveNotesBtnText}>Save Notes</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View>
          <View style={styles.notesDisplay}>
            {userNotes
              ? <Text style={styles.notesText}>{userNotes}</Text>
              : <Text style={styles.notesPlaceholder}>Tap "Edit Notes" to add your prep notes for this event.</Text>
            }
          </View>
          <TouchableOpacity style={styles.editNotesBtn} onPress={() => setEditingNotes(true)}>
            <MaterialCommunityIcons name="pencil" size={16} color="#0e4e96" />
            <Text style={styles.editNotesBtnText}>{userNotes ? 'Edit Notes' : 'Add Notes'}</Text>
          </TouchableOpacity>
        </View>
      )}
      <Text style={styles.sectionTitle}>Prep Checklist</Text>
      {['Research exhibiting companies', 'Prepare elevator pitch (60 sec)', 'Update CV and LinkedIn', 'Print CV copies', 'Plan travel / accommodation', 'Set event reminders', 'Prepare questions for speakers', 'LinkedIn QR code ready'].map((item, i) => (
        <View key={i} style={styles.checklistItem}>
          <MaterialCommunityIcons name="checkbox-blank-circle-outline" size={18} color="#0e4e96" />
          <Text style={styles.checklistText}>{item}</Text>
        </View>
      ))}
    </View>
  );

  const renderQATab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Frequently Asked Questions</Text>
      {eventDetails.faqs.map((faq, i) => (
        <TouchableOpacity key={i} style={styles.faqItem} onPress={() => setExpandedFaq(expandedFaq === i ? null : i)}>
          <View style={styles.faqQuestion}>
            <Text style={styles.faqQuestionText}>{faq.question}</Text>
            <MaterialIcons name={expandedFaq === i ? 'expand-less' : 'expand-more'} size={24} color="#0e4e96" />
          </View>
          {expandedFaq === i && <Text style={styles.faqAnswer}>{faq.answer}</Text>}
        </TouchableOpacity>
      ))}

      <Text style={styles.sectionTitle}>Ask a Question</Text>
      <View style={styles.questionCard}>
        <TextInput
          style={styles.questionInput}
          placeholder="Type your question to the organiser..."
          placeholderTextColor="#999"
          multiline
          numberOfLines={4}
          value={newQuestion}
          onChangeText={setNewQuestion}
        />
        <TouchableOpacity style={styles.submitQuestionBtn} onPress={submitQuestion}>
          <MaterialCommunityIcons name="send" size={16} color="#fff" />
          <Text style={styles.submitQuestionText}>Submit Question</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderTabContent = () => {
    if (!eventDetails) return null;
    switch (activeTab) {
      case 'About':     return renderAboutTab();
      case 'Agenda':    return renderAgendaTab();
      case 'Speakers':  return renderSpeakersTab();
      case 'Exhibitors':return renderExhibitorsTab();
      case 'Photos':    return renderPhotosTab();
      case 'Travel':    return renderTravelTab();
      case 'Reviews':   return renderReviewsTab();
      case 'Notes':     return renderNotesTab();
      case 'Q&A':       return renderQATab();
      default:          return renderAboutTab();
    }
  };

  // ── Loading / Error ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Event Details</Text>
          <View style={{ width: 24 }} />
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={40} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading event details...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!eventDetails) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Event Details</Text>
          <View style={{ width: 24 }} />
        </View>
        <View style={styles.errorContainer}>
          <MaterialCommunityIcons name="alert-circle" size={64} color="#ccc" />
          <Text style={styles.errorText}>Event not found</Text>
          <TouchableOpacity style={styles.retryButton} onPress={loadEventDetails}>
            <Text style={styles.retryButtonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const color   = getEventColor(eventDetails.type);
  const isFree  = (eventDetails.price || '').toLowerCase().includes('free');
  const isOnline = eventDetails.isOnline || eventDetails.location?.toLowerCase().includes('online');

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* ── Header ── */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Event Details</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity onPress={() => setNotifyEnabled(!notifyEnabled)} style={styles.headerBtn}>
            <MaterialCommunityIcons name={notifyEnabled ? 'bell' : 'bell-outline'} size={22} color={notifyEnabled ? '#fbbf24' : '#fff'} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setIsSaved(!isSaved)} style={styles.headerBtn}>
            <MaterialCommunityIcons name={isSaved ? 'bookmark' : 'bookmark-outline'} size={22} color={isSaved ? '#fbbf24' : '#fff'} />
          </TouchableOpacity>
          <TouchableOpacity onPress={handleShare} style={styles.headerBtn}>
            <Ionicons name="share-outline" size={22} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView style={styles.scrollView}>
        {/* ── Banner ── */}
        <View style={[styles.banner, { backgroundColor: color }]}>
          <MaterialCommunityIcons name={getEventIcon(eventDetails.type)} size={64} color="rgba(255,255,255,0.9)" />
          <View style={styles.bannerBadges}>
            <View style={styles.bannerTypeBadge}><Text style={styles.bannerTypeBadgeText}>{eventDetails.type}</Text></View>
            {isOnline && <View style={[styles.bannerTypeBadge, { backgroundColor: '#10b981' }]}><Text style={styles.bannerTypeBadgeText}>Online</Text></View>}
            {eventDetails.isFeatured && <View style={[styles.bannerTypeBadge, { backgroundColor: '#f59e0b' }]}><MaterialCommunityIcons name="star" size={10} color="#fff" /><Text style={styles.bannerTypeBadgeText}> Featured</Text></View>}
          </View>
          <View style={styles.bannerSourceBadge}><Text style={styles.bannerSourceText}>{eventDetails.source}</Text></View>
        </View>

        {/* ── Title Card ── */}
        <View style={styles.titleCard}>
          <Text style={styles.eventTitle}>{eventDetails.title}</Text>
          <View style={styles.organizerRow}>
            <MaterialCommunityIcons name="account-group" size={16} color="#666" />
            <Text style={styles.organizerText}>Organised by {eventDetails.organizer}</Text>
          </View>

          {/* Countdown */}
          {!countdown.isPast && (
            <View style={styles.countdownContainer}>
              <Text style={styles.countdownLabel}>Event starts in</Text>
              <View style={styles.countdownRow}>
                {[
                  { value: countdown.days,    label: 'days' },
                  { value: countdown.hours,   label: 'hrs' },
                  { value: countdown.minutes, label: 'min' },
                  { value: countdown.seconds, label: 'sec' },
                ].map((c, i) => (
                  <View key={i} style={styles.countdownBox}>
                    <Text style={styles.countdownValue}>{String(c.value).padStart(2,'0')}</Text>
                    <Text style={styles.countdownUnit}>{c.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}
          {countdown.isPast && (
            <View style={styles.pastEventBanner}>
              <MaterialCommunityIcons name="history" size={18} color="#666" />
              <Text style={styles.pastEventText}>This event has already taken place</Text>
            </View>
          )}

          {/* AI Advice */}
          <View style={styles.aiCard}>
            <View style={styles.aiCardHeader}>
              <View style={styles.aiCardIcon}><MaterialCommunityIcons name="robot" size={18} color="#fff" /></View>
              <Text style={styles.aiCardTitle}>AI Career Strategy</Text>
              <View style={styles.expLevelRow}>
                {['graduate','mid','senior'].map(l => (
                  <TouchableOpacity key={l} style={[styles.expBtn, experienceLevel === l && styles.expBtnActive]}
                    onPress={() => { setExperienceLevel(l); setAiAdvice(''); loadAiAdvice(); }}>
                    <Text style={[styles.expBtnText, experienceLevel === l && styles.expBtnTextActive]}>{l}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            {isAiLoading
              ? <ActivityIndicator size={20} color="#19abb5" style={{ margin: 12 }} />
              : <Text style={styles.aiAdviceText}>{aiAdvice}</Text>
            }
          </View>

          {/* Registration Status */}
          {eventDetails.isRegistered && (
            <View style={styles.registeredBanner}>
              <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
              <Text style={styles.registeredBannerText}>You are registered for this event</Text>
            </View>
          )}

          {/* CTA Buttons */}
          <TouchableOpacity style={[styles.primaryBtn, { backgroundColor: eventDetails.isRegistered ? '#ef4444' : color }]} onPress={handleRegister}>
            <MaterialCommunityIcons name={eventDetails.isRegistered ? 'close-circle' : 'clipboard-check'} size={20} color="#fff" />
            <Text style={styles.primaryBtnText}>{eventDetails.isRegistered ? 'Cancel Registration' : 'Register Now — ' + (eventDetails.price || 'Free')}</Text>
          </TouchableOpacity>

          <View style={styles.secondaryBtns}>
            {[
              { icon: 'calendar-plus', label: 'Calendar', action: handleAddToCalendar },
              { icon: 'web', label: 'Website', action: () => Linking.openURL(eventDetails.website) },
              { icon: isOnline ? 'video' : 'map-marker', label: isOnline ? 'Join' : 'Map', action: isOnline ? () => Linking.openURL(eventDetails.onlineLink) : handleOpenMap },
              { icon: 'share-variant', label: 'Share', action: handleShare },
            ].map(btn => (
              <TouchableOpacity key={btn.label} style={styles.secondaryBtn} onPress={btn.action}>
                <MaterialCommunityIcons name={btn.icon} size={20} color={color} />
                <Text style={[styles.secondaryBtnText, { color }]}>{btn.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* ── Tabs ── */}
        <View style={styles.tabBar}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabScroll}>
            {TABS.map(tab => (
              <TouchableOpacity key={tab} style={[styles.tab, activeTab === tab && styles.tabActive]} onPress={() => setActiveTab(tab)}>
                <Text style={[styles.tabText, activeTab === tab && styles.tabTextActive]}>{tab}</Text>
                {tab === 'Reviews' && reviews.length > 0 && (
                  <View style={styles.tabBadge}><Text style={styles.tabBadgeText}>{reviews.length}</Text></View>
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* ── Tab Content ── */}
        {renderTabContent()}

        {/* ── Related Events ── */}
        {eventDetails.relatedEvents?.length > 0 && (
          <View style={styles.relatedSection}>
            <Text style={styles.sectionTitle}>Related Events</Text>
            {eventDetails.relatedEvents.map(re => (
              <TouchableOpacity key={re.id} style={styles.relatedCard} onPress={() => navigation.navigate('EventDetail', { eventId: re.id })}>
                <View style={[styles.relatedIcon, { backgroundColor: getEventColor(re.type) + '22' }]}>
                  <MaterialCommunityIcons name={getEventIcon(re.type)} size={22} color={getEventColor(re.type)} />
                </View>
                <View style={styles.relatedInfo}>
                  <Text style={styles.relatedTitle}>{re.title}</Text>
                  <Text style={styles.relatedMeta}>{formatShortDate(re.date)} • {re.location}</Text>
                </View>
                <MaterialIcons name="chevron-right" size={22} color="#999" />
              </TouchableOpacity>
            ))}
          </View>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>

      {/* ── Share Modal ── */}
      <Modal visible={showShareModal} transparent animationType="slide">
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowShareModal(false)}>
          <View style={styles.shareModalContent}>
            <Text style={styles.shareModalTitle}>Share Event</Text>
            {[
              { icon: 'share-variant', label: 'Share via...', color: '#0e4e96', method: 'native' },
              { icon: 'whatsapp',      label: 'WhatsApp',     color: '#25D366', method: 'whatsapp' },
              { icon: 'linkedin',      label: 'LinkedIn',     color: '#0A66C2', method: 'linkedin' },
              { icon: 'email',         label: 'Email',        color: '#ea4335', method: 'email' },
              { icon: 'content-copy',  label: 'Copy Link',    color: '#6b7280', method: 'copy' },
            ].map(s => (
              <TouchableOpacity key={s.method} style={styles.shareOption} onPress={() => shareVia(s.method)}>
                <View style={[styles.shareOptionIcon, { backgroundColor: s.color }]}>
                  <MaterialCommunityIcons name={s.icon} size={20} color="#fff" />
                </View>
                <Text style={styles.shareOptionText}>{s.label}</Text>
                <MaterialIcons name="chevron-right" size={20} color="#ccc" />
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* ── Registration Confirmation Modal ── */}
      <Modal visible={showRegModal} transparent animationType="fade">
        <View style={styles.regModalOverlay}>
          <View style={styles.regModalContent}>
            <MaterialCommunityIcons name="calendar-check" size={56} color="#0e4e96" />
            <Text style={styles.regModalTitle}>Confirm Registration</Text>
            <Text style={styles.regModalEvent}>{eventDetails.title}</Text>
            <Text style={styles.regModalDate}>{formatDate(eventDetails.date)}</Text>
            <Text style={styles.regModalLocation}>{eventDetails.location}</Text>
            <View style={styles.regConfirmationBox}>
              <Text style={styles.regConfirmLabel}>Confirmation #</Text>
              <Text style={styles.regConfirmNumber}>{confirmationNo}</Text>
            </View>
            <Text style={styles.regModalNote}>A confirmation will be sent to your registered email address.</Text>
            <View style={styles.regModalBtns}>
              <TouchableOpacity style={styles.regCancelBtn} onPress={() => setShowRegModal(false)}>
                <Text style={styles.regCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.regConfirmBtn, { backgroundColor: color }]} onPress={confirmRegister}>
                <Text style={styles.regConfirmText}>Confirm</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Photo Lightbox Modal ── */}
      <Modal visible={photoModal} transparent animationType="fade">
        <TouchableOpacity style={styles.photoModalOverlay} onPress={() => setPhotoModal(false)}>
          <View style={styles.photoModalContent}>
            {selectedPhoto?.url
              ? <Image source={{ uri: selectedPhoto.url }} style={styles.photoModalImg} resizeMode="contain" />
              : <View style={styles.photoModalPlaceholder}><MaterialCommunityIcons name="image-outline" size={80} color="rgba(255,255,255,0.5)" /></View>
            }
            {selectedPhoto && <Text style={styles.photoModalCaption}>{selectedPhoto.caption}</Text>}
          </View>
        </TouchableOpacity>
      </Modal>
    </SafeAreaView>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  safeArea:              { flex: 1, backgroundColor: '#0e4e96' },
  header:                { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#0e4e96', paddingVertical: 14, paddingHorizontal: 15 },
  backBtn:               { padding: 6 },
  headerTitle:           { color: '#fff', fontSize: 17, fontWeight: 'bold', flex: 1, marginLeft: 8 },
  headerActions:         { flexDirection: 'row' },
  headerBtn:             { padding: 6, marginLeft: 4 },
  scrollView:            { flex: 1, backgroundColor: '#f5f6fa' },
  // Loading / error
  loadingContainer:      { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f5f6fa' },
  loadingText:           { marginTop: 14, fontSize: 15, color: '#666' },
  errorContainer:        { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f5f6fa', padding: 20 },
  errorText:             { fontSize: 18, color: '#666', marginTop: 10, marginBottom: 20 },
  retryButton:           { backgroundColor: '#0e4e96', paddingHorizontal: 28, paddingVertical: 12, borderRadius: 10 },
  retryButtonText:       { color: '#fff', fontWeight: 'bold', fontSize: 15 },
  // Banner
  banner:                { height: 200, justifyContent: 'center', alignItems: 'center', position: 'relative' },
  bannerBadges:          { position: 'absolute', top: 14, left: 14, flexDirection: 'row', gap: 6 },
  bannerTypeBadge:       { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.6)', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14 },
  bannerTypeBadgeText:   { color: '#fff', fontSize: 11, fontWeight: 'bold' },
  bannerSourceBadge:     { position: 'absolute', top: 14, right: 14, backgroundColor: '#fdb913', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 10 },
  bannerSourceText:      { color: '#fff', fontSize: 10, fontWeight: 'bold' },
  // Title card
  titleCard:             { backgroundColor: '#fff', padding: 20, marginBottom: 4 },
  eventTitle:            { fontSize: 22, fontWeight: 'bold', color: '#0e4e96', marginBottom: 8, lineHeight: 28 },
  organizerRow:          { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  organizerText:         { fontSize: 13, color: '#666', marginLeft: 6 },
  // Countdown
  countdownContainer:    { backgroundColor: '#f0f4ff', borderRadius: 12, padding: 14, marginBottom: 16, alignItems: 'center' },
  countdownLabel:        { fontSize: 12, color: '#0e4e96', fontWeight: '600', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 },
  countdownRow:          { flexDirection: 'row', gap: 8 },
  countdownBox:          { backgroundColor: '#0e4e96', borderRadius: 10, width: 56, height: 56, alignItems: 'center', justifyContent: 'center' },
  countdownValue:        { fontSize: 20, fontWeight: 'bold', color: '#fff' },
  countdownUnit:         { fontSize: 10, color: 'rgba(255,255,255,0.7)' },
  pastEventBanner:       { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f3f4f6', padding: 12, borderRadius: 10, marginBottom: 16 },
  pastEventText:         { color: '#666', marginLeft: 8, fontSize: 13 },
  // AI Card
  aiCard:                { backgroundColor: '#f0fdfc', borderRadius: 12, padding: 14, marginBottom: 16, borderLeftWidth: 3, borderLeftColor: '#19abb5' },
  aiCardHeader:          { flexDirection: 'row', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 6 },
  aiCardIcon:            { width: 30, height: 30, borderRadius: 15, backgroundColor: '#19abb5', justifyContent: 'center', alignItems: 'center' },
  aiCardTitle:           { fontSize: 14, fontWeight: 'bold', color: '#0e7490', flex: 1 },
  expLevelRow:           { flexDirection: 'row', gap: 4 },
  expBtn:                { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10, backgroundColor: '#e0f7fa' },
  expBtnActive:          { backgroundColor: '#0e4e96' },
  expBtnText:            { fontSize: 10, color: '#0e7490', fontWeight: '600' },
  expBtnTextActive:      { color: '#fff' },
  aiAdviceText:          { fontSize: 13, color: '#1a3a3a', lineHeight: 20 },
  // Registered banner
  registeredBanner:      { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f0fdf4', padding: 12, borderRadius: 10, marginBottom: 14 },
  registeredBannerText:  { color: '#166534', marginLeft: 8, fontWeight: '600', fontSize: 13 },
  // CTAs
  primaryBtn:            { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 15, borderRadius: 12, marginBottom: 12 },
  primaryBtnText:        { color: '#fff', fontWeight: 'bold', fontSize: 16, marginLeft: 8 },
  secondaryBtns:         { flexDirection: 'row', justifyContent: 'space-between' },
  secondaryBtn:          { flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 12, borderRadius: 10, backgroundColor: '#f5f6fa', marginHorizontal: 3 },
  secondaryBtnText:      { fontSize: 11, fontWeight: '600', marginTop: 4 },
  // Tab bar
  tabBar:                { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#eee' },
  tabScroll:             { paddingHorizontal: 12 },
  tab:                   { paddingHorizontal: 16, paddingVertical: 14, position: 'relative', flexDirection: 'row', alignItems: 'center' },
  tabActive:             { borderBottomWidth: 2, borderBottomColor: '#0e4e96' },
  tabText:               { fontSize: 14, color: '#888', fontWeight: '500' },
  tabTextActive:         { color: '#0e4e96', fontWeight: 'bold' },
  tabBadge:              { backgroundColor: '#0e4e96', borderRadius: 8, paddingHorizontal: 5, paddingVertical: 1, marginLeft: 4 },
  tabBadgeText:          { color: '#fff', fontSize: 10, fontWeight: 'bold' },
  // Tab content
  tabContent:            { backgroundColor: '#fff', padding: 20, marginBottom: 4 },
  sectionTitle:          { fontSize: 17, fontWeight: 'bold', color: '#1a1a2e', marginTop: 8, marginBottom: 12 },
  description:           { fontSize: 14, color: '#444', lineHeight: 22, marginBottom: 20 },
  // Details card
  detailsCard:           { backgroundColor: '#f8f9fa', borderRadius: 12, padding: 14, marginBottom: 20 },
  detailItem:            { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#eee' },
  detailIconWrap:        { width: 36, height: 36, backgroundColor: '#e8f0fe', borderRadius: 10, justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  detailText:            { fontSize: 14, color: '#333', flex: 1 },
  // Capacity bar
  capacityContainer:     { marginBottom: 20 },
  capacityRow:           { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 },
  capacityLabel:         { fontSize: 13, color: '#666', fontWeight: '600' },
  capacityPct:           { fontSize: 13, color: '#0e4e96', fontWeight: '700' },
  capacityBar:           { height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden', marginBottom: 4 },
  capacityFill:          { height: '100%', borderRadius: 4 },
  capacityNote:          { fontSize: 11, color: '#999' },
  // Organiser
  organizerCard:         { flexDirection: 'row', backgroundColor: '#f8f9fa', borderRadius: 12, padding: 14, marginBottom: 20 },
  organizerIcon:         { width: 52, height: 52, backgroundColor: '#e8f0fe', borderRadius: 12, justifyContent: 'center', alignItems: 'center', marginRight: 14 },
  organizerInfo:         { flex: 1 },
  organizerName:         { fontSize: 15, fontWeight: 'bold', color: '#1a1a2e', marginBottom: 8 },
  contactRow:            { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  contactBtn:            { flexDirection: 'row', alignItems: 'center', backgroundColor: '#0e4e96', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8 },
  contactBtnText:        { color: '#fff', fontSize: 12, fontWeight: '600', marginLeft: 4 },
  // Badges
  badgeWrap:             { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 20 },
  sectorBadge:           { backgroundColor: '#e8f0fe', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 10 },
  sectorBadgeText:       { fontSize: 12, color: '#0e4e96', fontWeight: '600' },
  qualBadge:             { backgroundColor: '#fef3c7', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 10 },
  qualBadgeText:         { fontSize: 12, color: '#92400e', fontWeight: '700' },
  bodyBadge:             { backgroundColor: '#f0fdf4', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 10 },
  bodyBadgeText:         { fontSize: 12, color: '#166534', fontWeight: '600' },
  // Documents
  documentRow:           { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8f9fa', borderRadius: 10, padding: 12, marginBottom: 10 },
  documentInfo:          { flex: 1, marginLeft: 12 },
  documentName:          { fontSize: 14, fontWeight: '600', color: '#333' },
  documentSize:          { fontSize: 12, color: '#999', marginTop: 2 },
  // Agenda
  agendaRow:             { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 14 },
  agendaDot:             { width: 10, height: 10, borderRadius: 5, marginTop: 4, marginRight: 10 },
  agendaTimeCol:         { width: 95 },
  agendaTime:            { fontSize: 13, fontWeight: 'bold', color: '#0e4e96' },
  agendaActivityCol:     { flex: 1 },
  agendaActivity:        { fontSize: 14, color: '#333', lineHeight: 20 },
  agendaTypeBadge:       { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8, marginTop: 4 },
  agendaTypeText:        { fontSize: 10, fontWeight: '700' },
  showMoreBtn:           { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 12, backgroundColor: '#f0f4ff', borderRadius: 10, marginTop: 8 },
  showMoreText:          { color: '#0e4e96', fontWeight: '600', marginRight: 4, fontSize: 13 },
  // Speakers
  speakerCard:           { flexDirection: 'row', marginBottom: 20, paddingBottom: 20, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  speakerAvatarWrap:     { marginRight: 14 },
  speakerAvatar:         { width: 68, height: 68, borderRadius: 34 },
  speakerAvatarPlaceholder: { width: 68, height: 68, borderRadius: 34, backgroundColor: '#0e4e96', justifyContent: 'center', alignItems: 'center' },
  speakerInitial:        { color: '#fff', fontSize: 24, fontWeight: 'bold' },
  speakerInfo:           { flex: 1 },
  speakerNameRow:        { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  speakerName:           { fontSize: 15, fontWeight: 'bold', color: '#1a1a2e', flex: 1 },
  speakerTitle:          { fontSize: 12, color: '#666', marginBottom: 6 },
  speakerTopic:          { fontSize: 13, fontWeight: '600', color: '#0e4e96', marginBottom: 6, fontStyle: 'italic' },
  speakerTimeRow:        { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  speakerTimeText:       { fontSize: 12, color: '#666', marginLeft: 4 },
  speakerBio:            { fontSize: 12, color: '#777', lineHeight: 18 },
  // Exhibitors
  exhibitorCount:        { fontSize: 13, color: '#666', marginBottom: 14 },
  exhibitorsGrid:        { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 14 },
  exhibitorChip:         { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f0f4ff', paddingHorizontal: 10, paddingVertical: 7, borderRadius: 10, maxWidth: (width - 56) / 2 },
  exhibitorChipText:     { fontSize: 12, color: '#0e4e96', fontWeight: '500', marginLeft: 6, flex: 1 },
  // Photos
  gallerySubtitle:       { fontSize: 13, color: '#999', marginBottom: 14 },
  photoGrid:             { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  photoThumb:            { width: (width - 60) / 2, borderRadius: 12, overflow: 'hidden' },
  photoThumbImg:         { width: '100%', height: 120 },
  photoThumbPlaceholder: { width: '100%', height: 120, justifyContent: 'center', alignItems: 'center' },
  photoCaption:          { fontSize: 11, color: '#666', padding: 6, backgroundColor: '#f8f9fa' },
  // Travel
  onlineCard:            { backgroundColor: '#f0fdf4', borderRadius: 14, padding: 24, alignItems: 'center', marginBottom: 20 },
  onlineTitle:           { fontSize: 16, fontWeight: 'bold', color: '#166534', marginTop: 12, marginBottom: 14 },
  onlineLinkBtn:         { flexDirection: 'row', alignItems: 'center', backgroundColor: '#10b981', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 10 },
  onlineLinkText:        { color: '#fff', fontWeight: 'bold', marginLeft: 8 },
  mapContainer:          { height: 200, borderRadius: 12, overflow: 'hidden', marginBottom: 14 },
  map:                   { width: '100%', height: '100%' },
  addressLabel:          { fontSize: 14, fontWeight: 'bold', color: '#333', marginBottom: 4 },
  addressText:           { fontSize: 14, color: '#666', marginBottom: 14 },
  directionsBtn:         { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', paddingVertical: 13, borderRadius: 12, marginBottom: 20 },
  directionsBtnText:     { color: '#fff', fontWeight: 'bold', marginLeft: 8, fontSize: 15 },
  transportCard:         { backgroundColor: '#f8f9fa', borderRadius: 12, padding: 14, marginBottom: 20 },
  transportRow:          { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  transportIcon:         { width: 28, height: 28, borderRadius: 8, justifyContent: 'center', alignItems: 'center', marginRight: 10 },
  transportText:         { fontSize: 13, color: '#444', flex: 1 },
  hotelRow:              { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  hotelInfo:             { flex: 1, marginLeft: 10 },
  hotelName:             { fontSize: 14, fontWeight: '600', color: '#333' },
  hotelNote:             { fontSize: 12, color: '#999', marginTop: 2 },
  // Reviews
  ratingsSummary:        { alignItems: 'center', marginBottom: 24 },
  avgRatingBox:          { alignItems: 'center', backgroundColor: '#f8f9fa', borderRadius: 16, padding: 20 },
  avgRatingNumber:       { fontSize: 48, fontWeight: 'bold', color: '#0e4e96', lineHeight: 52 },
  starsRow:              { flexDirection: 'row', gap: 2, marginVertical: 6 },
  avgRatingCount:        { fontSize: 13, color: '#999' },
  reviewForm:            { backgroundColor: '#f8f9fa', borderRadius: 14, padding: 16, marginBottom: 24 },
  reviewFormLabel:       { fontSize: 14, fontWeight: '600', color: '#333', marginBottom: 8 },
  reviewInput:           { backgroundColor: '#fff', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 10, padding: 12, fontSize: 14, color: '#333', minHeight: 90, textAlignVertical: 'top', marginTop: 10, marginBottom: 12 },
  submitReviewBtn:       { backgroundColor: '#0e4e96', paddingVertical: 12, borderRadius: 10, alignItems: 'center' },
  submitReviewText:      { color: '#fff', fontWeight: 'bold', fontSize: 14 },
  reviewCard:            { backgroundColor: '#f8f9fa', borderRadius: 12, padding: 14, marginBottom: 12 },
  reviewHeader:          { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  reviewAuthorInfo:      { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  reviewAvatar:          { width: 36, height: 36, borderRadius: 18, backgroundColor: '#0e4e96', justifyContent: 'center', alignItems: 'center' },
  reviewAvatarText:      { color: '#fff', fontWeight: 'bold', fontSize: 16 },
  reviewNameRow:         { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 },
  reviewAuthor:          { fontSize: 14, fontWeight: 'bold', color: '#1a1a2e' },
  verifiedBadge:         { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f0fdf4', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8 },
  verifiedText:          { fontSize: 10, color: '#166534', marginLeft: 2, fontWeight: '600' },
  reviewDate:            { fontSize: 11, color: '#999' },
  reviewComment:         { fontSize: 13, color: '#444', lineHeight: 19 },
  // Notes
  notesSubtitle:         { fontSize: 13, color: '#999', marginBottom: 16 },
  notesInput:            { backgroundColor: '#f8f9fa', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 12, padding: 14, fontSize: 14, color: '#333', minHeight: 180, textAlignVertical: 'top', marginBottom: 12 },
  saveNotesBtn:          { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', paddingVertical: 12, borderRadius: 10 },
  saveNotesBtnText:      { color: '#fff', fontWeight: 'bold', marginLeft: 6 },
  notesDisplay:          { backgroundColor: '#f8f9fa', borderRadius: 12, padding: 16, minHeight: 100, marginBottom: 12 },
  notesText:             { fontSize: 14, color: '#333', lineHeight: 22 },
  notesPlaceholder:      { fontSize: 14, color: '#bbb', fontStyle: 'italic' },
  editNotesBtn:          { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 14, borderWidth: 1.5, borderColor: '#0e4e96', borderRadius: 10, alignSelf: 'flex-start', marginBottom: 24 },
  editNotesBtnText:      { color: '#0e4e96', fontWeight: '600', marginLeft: 6 },
  checklistItem:         { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  checklistText:         { fontSize: 14, color: '#333', marginLeft: 10, flex: 1 },
  // FAQ
  faqItem:               { borderBottomWidth: 1, borderBottomColor: '#f0f0f0', paddingVertical: 12 },
  faqQuestion:           { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  faqQuestionText:       { fontSize: 14, fontWeight: '600', color: '#333', flex: 1, paddingRight: 8 },
  faqAnswer:             { fontSize: 14, color: '#555', lineHeight: 21, marginTop: 8, paddingBottom: 4 },
  questionCard:          { backgroundColor: '#f8f9fa', borderRadius: 12, padding: 14 },
  questionInput:         { backgroundColor: '#fff', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 10, padding: 12, fontSize: 14, color: '#333', minHeight: 100, textAlignVertical: 'top', marginBottom: 10 },
  submitQuestionBtn:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#19abb5', paddingVertical: 12, borderRadius: 10 },
  submitQuestionText:    { color: '#fff', fontWeight: 'bold', marginLeft: 6 },
  // Related
  relatedSection:        { backgroundColor: '#fff', padding: 20, borderTopWidth: 1, borderTopColor: '#eee' },
  relatedCard:           { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  relatedIcon:           { width: 44, height: 44, borderRadius: 12, justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  relatedInfo:           { flex: 1 },
  relatedTitle:          { fontSize: 14, fontWeight: 'bold', color: '#0e4e96', marginBottom: 3 },
  relatedMeta:           { fontSize: 12, color: '#888' },
  // Share modal
  modalOverlay:          { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  shareModalContent:     { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24 },
  shareModalTitle:       { fontSize: 18, fontWeight: 'bold', color: '#1a1a2e', marginBottom: 16 },
  shareOption:           { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  shareOptionIcon:       { width: 40, height: 40, borderRadius: 12, justifyContent: 'center', alignItems: 'center', marginRight: 14 },
  shareOptionText:       { flex: 1, fontSize: 15, color: '#333', fontWeight: '500' },
  // Registration modal
  regModalOverlay:       { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  regModalContent:       { backgroundColor: '#fff', borderRadius: 20, padding: 28, width: '100%', alignItems: 'center' },
  regModalTitle:         { fontSize: 22, fontWeight: 'bold', color: '#0e4e96', marginTop: 12, marginBottom: 8 },
  regModalEvent:         { fontSize: 15, fontWeight: '600', color: '#333', textAlign: 'center', marginBottom: 4 },
  regModalDate:          { fontSize: 13, color: '#666', marginBottom: 2 },
  regModalLocation:      { fontSize: 13, color: '#888', marginBottom: 16 },
  regConfirmationBox:    { backgroundColor: '#f0f4ff', borderRadius: 12, padding: 14, width: '100%', alignItems: 'center', marginBottom: 12 },
  regConfirmLabel:       { fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5 },
  regConfirmNumber:      { fontSize: 20, fontWeight: 'bold', color: '#0e4e96', marginTop: 4 },
  regModalNote:          { fontSize: 12, color: '#999', textAlign: 'center', marginBottom: 20 },
  regModalBtns:          { flexDirection: 'row', gap: 10, width: '100%' },
  regCancelBtn:          { flex: 1, paddingVertical: 13, borderRadius: 12, borderWidth: 1.5, borderColor: '#e5e7eb', alignItems: 'center' },
  regCancelText:         { color: '#666', fontWeight: '600', fontSize: 15 },
  regConfirmBtn:         { flex: 1, paddingVertical: 13, borderRadius: 12, alignItems: 'center' },
  regConfirmText:        { color: '#fff', fontWeight: 'bold', fontSize: 15 },
  // Photo lightbox
  photoModalOverlay:     { flex: 1, backgroundColor: 'rgba(0,0,0,0.9)', justifyContent: 'center', alignItems: 'center' },
  photoModalContent:     { width: '95%', alignItems: 'center' },
  photoModalImg:         { width: '100%', height: 360, borderRadius: 12 },
  photoModalPlaceholder: { width: '100%', height: 360, backgroundColor: '#1a1a2e', borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  photoModalCaption:     { color: '#fff', fontSize: 14, marginTop: 12, fontWeight: '500' },
});
